import React, { useState, useEffect, useCallback, useRef } from 'react';

const colors = {
  bg: '#020617', cardBg: '#0f172a', border: '#1e293b',
  textMain: '#f8fafc', textSub: '#94a3b8',
  green: '#22c55e', red: '#ef4444', blue: '#3b82f6', warn: '#eab308'
};

const styles = {
  container: { backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '16px', marginTop: '20px' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '15px' },
  inputGroup: { display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: colors.textSub },
  input: { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, color: 'white', padding: '6px 8px', borderRadius: '6px', width: '60px', outline: 'none' },
  saveBtn: { backgroundColor: colors.blue, color: 'white', border: 'none', padding: '8px 20px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px', boxShadow: '0 4px 6px rgba(0,0,0,0.2)' },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', maxHeight: '390px', overflowY: 'auto' },
  coinCard: { backgroundColor: colors.bg, border: `1px solid ${colors.border}`, padding: '12px', borderRadius: '10px', cursor: 'pointer', transition: 'transform 0.1s' },
  label: { cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', userSelect: 'none' }
};

export default function SmartCoinFilterV2({ onSelectCoin }) {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  // [ĐÃ UPDATE]: Settings chính thức với From (Min) và To (Max)
  const [appliedSettings, setAppliedSettings] = useState({
    useVol: true, minVolume: 1000, maxVolume: '', // Để chuỗi rỗng mặc định cho ô Max
    usePrice: false, minPrice: 0,
    useChange: false, minChangePct: 5, maxChangePct: '',
    lookbackDays: 3
  });

  const [tempSettings, setTempSettings] = useState(appliedSettings);

  const fetchMarketData = useCallback(async () => {
    try {
      setLoading(true);
      const tickerRes = await fetch('https://fapi.binance.com/fapi/v1/ticker/24hr');
      const tickerData = await tickerRes.json();

      const activeSymbols = tickerData
        .filter(t => t.symbol.endsWith('USDT'))
        .sort((a, b) => parseFloat(b.quoteVolume) - parseFloat(a.quoteVolume))
        .slice(0, 80); 

      const fullData = await Promise.all(activeSymbols.map(async (coin) => {
        try {
          const limit = appliedSettings.lookbackDays;
          const klineRes = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${coin.symbol}&interval=1d&limit=${limit}`);
          const klines = await klineRes.json();

          if (!klines || !Array.isArray(klines) || klines.length < limit) return null;

          // =========================================================================
          // 🛠️ TÍNH TOÁN ĐẦY ĐỦ THÔNG SỐ OHLC THEO TOÀN BỘ CHU KỲ LOOKBACKDAYS
          // =========================================================================
          const openPrice = parseFloat(klines[0][1]);                               // Giá Open của ngày cũ nhất
          const highPrice = Math.max(...klines.map(k => parseFloat(k[2])));         // Đỉnh High cao nhất trong N ngày
          const lowPrice = Math.min(...klines.map(k => parseFloat(k[3])));          // Đáy Low thấp nhất trong N ngày
          const closePrice = parseFloat(klines[klines.length - 1][4]);              // Giá Close của nến ngày mới nhất
          const currentPrice = parseFloat(coin.lastPrice);
          const historicalChange = ((currentPrice - openPrice) / openPrice) * 100;

          return {
            ...coin,
            openPrice,
            highPrice,
            lowPrice,
            closePrice,
            openPriceN: openPrice,
            highPriceN: highPrice,
            lowPriceN: lowPrice,
            closePriceN: closePrice,
            historicalChange,
            volM: parseFloat(coin.quoteVolume) / 1000000
          };
        } catch (e) { return null; }
      }));

      // [ĐÃ UPDATE]: Logic lọc xử lý khoảng From - To
      const finalFiltered = fullData.filter(coin => {
        if (!coin) return false;
        
        let match = true;

        // Xử lý Lọc Volume (Từ - Đến)
        if (appliedSettings.useVol) {
          const minV = parseFloat(appliedSettings.minVolume);
          const maxV = parseFloat(appliedSettings.maxVolume);
          if (!isNaN(minV) && coin.volM < minV) match = false;
          if (!isNaN(maxV) && coin.volM > maxV) match = false;
        }

        // Xử lý Lọc Giá (Tối thiểu)
        if (appliedSettings.usePrice && parseFloat(coin.lastPrice) < parseFloat(appliedSettings.minPrice)) {
            match = false;
        }

        // Xử lý Lọc Biên độ giá trị tuyệt đối (Từ - Đến)
        if (appliedSettings.useChange) {
          const absChange = Math.abs(coin.historicalChange);
          const minC = parseFloat(appliedSettings.minChangePct);
          const maxC = parseFloat(appliedSettings.maxChangePct);
          
          if (!isNaN(minC) && absChange < minC) match = false;
          if (!isNaN(maxC) && absChange > maxC) match = false;
        }

        return match;
      }).sort((a, b) => b.volM - a.volM);

      setCoins(finalFiltered);
      setLastUpdate(new Date().toLocaleTimeString());
      setLoading(false);
    } catch (error) {
      console.error("Filter Error:", error);
      setLoading(false);
    }
  }, [appliedSettings]);

  useEffect(() => {
    fetchMarketData();
  }, [fetchMarketData]);

  const handleSave = () => setAppliedSettings(tempSettings);

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
          <strong style={{fontSize: '15px', color: colors.textMain}}>💎 SMART RADAR FILTER</strong>
          {loading && <div className="spinner" style={{width: '12px', height: '12px', border: '2px solid #3b82f6', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite'}}></div>}
        </div>

        <div style={{display: 'flex', gap: '15px', flexWrap: 'wrap', alignItems: 'center'}}>
          
          {/* VOLUME SETTING (FROM - TO) */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <input type="checkbox" checked={tempSettings.useVol} onChange={e => setTempSettings({...tempSettings, useVol: e.target.checked})}/>
              Vol (M):
            </label>
            <input type="number" placeholder="Min" value={tempSettings.minVolume} onChange={e => setTempSettings({...tempSettings, minVolume: e.target.value})} style={styles.input} />
            -
            <input type="number" placeholder="Max" value={tempSettings.maxVolume} onChange={e => setTempSettings({...tempSettings, maxVolume: e.target.value})} style={styles.input} />
          </div>

          {/* CHANGE SETTING (FROM - TO) */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <input type="checkbox" checked={tempSettings.useChange} onChange={e => setTempSettings({...tempSettings, useChange: e.target.checked})}/>
              Change (%):
            </label>
            <input type="number" placeholder="Min" value={tempSettings.minChangePct} onChange={e => setTempSettings({...tempSettings, minChangePct: e.target.value})} style={styles.input} />
            -
            <input type="number" placeholder="Max" value={tempSettings.maxChangePct} onChange={e => setTempSettings({...tempSettings, maxChangePct: e.target.value})} style={styles.input} />
            
            <span style={{marginLeft: '4px'}}>trong</span>
            <select value={tempSettings.lookbackDays} onChange={e => setTempSettings({...tempSettings, lookbackDays: parseInt(e.target.value)})} style={{...styles.input, width: '65px'}}>
              <option value={3}>3 ngày</option>
              <option value={5}>5 ngày</option>
              <option value={7}>7 ngày</option>
            </select>
          </div>

          {/* PRICE SETTING (Chỉ giữ Min) */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <input type="checkbox" checked={tempSettings.usePrice} onChange={e => setTempSettings({...tempSettings, usePrice: e.target.checked})}/>
              Price &gt;
            </label>
            <input type="number" value={tempSettings.minPrice} onChange={e => setTempSettings({...tempSettings, minPrice: e.target.value})} style={{...styles.input, width: '70px'}}/>$
          </div>
          
          <button onClick={handleSave} style={styles.saveBtn}>💾 SAVE SETTINGS</button>
        </div>
      </div>

      <div style={styles.grid}>
        {coins.map(coin => (
          <div key={coin.symbol} style={styles.coinCard} onClick={() => onSelectCoin(coin.symbol)} onMouseEnter={e => e.currentTarget.style.borderColor = colors.blue} onMouseLeave={e => e.currentTarget.style.borderColor = colors.border}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start'}}>
              <span style={{fontWeight: 'bold', fontSize: '15px', color: colors.textMain}}>{coin.symbol.replace('USDT', '')}</span>
              <div style={{textAlign: 'right'}}>
                <div style={{color: coin.historicalChange >= 0 ? colors.green : colors.red, fontWeight: 'bold', fontSize: '13px'}}>
                  {coin.historicalChange >= 0 ? '▲' : '▼'}{Math.abs(coin.historicalChange).toFixed(2)}%
                </div>
                <div style={{fontSize: '9px', color: colors.textSub}}>
                  {appliedSettings.lookbackDays}D O: ${fNum(coin.openPrice)}
                </div>
              </div>
            </div>
            
            <div style={{fontSize: '14px', margin: '6px 0 2px 0', color: colors.textMain}}>${parseFloat(coin.lastPrice).toFixed(4)}</div>

            {/* HIỂN THỊ THÊM ĐỈNH HIGH VÀ ĐÁY LOW CỦA CHU KỲ */}
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '9px', color: colors.textSub, marginBottom: '6px'}}>
              <span>H: <b style={{color: colors.green}}>${fNum(coin.highPrice)}</b></span>
              <span>L: <b style={{color: colors.red}}>${fNum(coin.lowPrice)}</b></span>
            </div>
            
            <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '11px', borderTop: `1px solid ${colors.border}`, paddingTop: '8px', color: colors.textSub}}>
              <span>Vol: <b style={{color: colors.textMain}}>{coin.volM.toFixed(0)}M</b></span>
              <span>24h: <b style={{color: parseFloat(coin.priceChangePercent) >= 0 ? colors.green : colors.red}}>{coin.priceChangePercent}%</b></span>
            </div>
          </div>
        ))}
        {!loading && coins.length === 0 && <div style={{gridColumn: '1/-1', textAlign: 'center', padding: '40px', color: colors.textSub}}>Không có dữ liệu thỏa mãn bộ lọc hiện tại.</div>}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .spinner { display: inline-block; }
      `}</style>
    </div>
  );
}

const fNum = (val) => val ? val.toLocaleString(undefined, {maximumFractionDigits: 4}) : '0';
